import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, ArrowLeft, Plane, ArrowRightLeft, Shield, Plus, X, Clock,
  AlertTriangle, Sparkles, Check, Briefcase, Baby, Dog, Building2, Route as RouteIcon,
  Users, Luggage, FileText, Calendar, MapPin, CarFront, ChevronDown,
} from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { getBooking } from "../i18n/bookingTranslations";
import { TID } from "../lib/testIds";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { RouteMap, CurrentLocationButton } from "../components/RouteMap";
import { WaitTimeNote } from "../components/WaitTimeNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Helpers ---------------------------------------------------------------
const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const nowLocalIso = () => {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const isoLocal = (d) => {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 16);
};

const isAirport = (addr) => /airport|aeroport|flughafen|ZRH|BSL|GVA|EAP/i.test(addr || "");

// --- Small building blocks -------------------------------------------------
const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-3 border transition-all duration-300 text-[11px] tracking-widest2 uppercase ${
      active
        ? "border-bronze bg-bronze/[0.08] text-brand-platinum"
        : "border-brand-border text-brand-silver hover:border-brand-silver hover:text-brand-platinum"
    }`}
  >
    {children}
  </button>
);

const Counter = ({ value, min = 0, max = 10, onChange, testId }) => (
  <div className="flex items-center gap-3">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
            data-testid={`${testId}-dec`}
            className="w-10 h-10 border border-brand-border text-brand-platinum hover:border-bronze transition-colors">−</button>
    <span data-testid={`${testId}-value`} className="w-8 text-center text-brand-platinum tabular-nums">{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))}
            data-testid={`${testId}-inc`}
            className="w-10 h-10 border border-brand-border text-brand-platinum hover:border-bronze transition-colors">+</button>
  </div>
);

const StepRow = ({ label, sublabel, right, children }) => (
  <div className="border-b border-brand-border/60 py-6">
    <div className="flex items-baseline justify-between mb-3">
      <div>
        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">{label}</p>
        {sublabel && <p className="text-[10px] text-brand-dim mt-1">{sublabel}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// --- Copy (5 languages) ----------------------------------------------------
const UI = {
  de: {
    steps: ["Route", "Wann", "Wer & Was", "Extras", "Zusammenfassung"],
    step: "Schritt",
    of: "von",
    next: "Weiter", back: "Zurück", pay: "Zahlen und bestätigen",
    servicePicker: "Wählen Sie Ihren Service",
    ride: "Punkt-zu-Punkt", byHour: "Stundenweise", multi: "Mehrere Halte", corp: "Firma/Hotel", full: "Ganztägig",
    hoursPack: "Stundenpaket", pickHours: "Wählen Sie die Stunden",
    stopN: (n) => `Halt ${n}`, addStop: "Halt hinzufügen", swap: "Tauschen",
    when: "Wann?", date: "Datum", time: "Uhrzeit", useNext: "Nächsten freien Termin verwenden",
    passengers: "Personen", luggageTitle: "Gepäck", childSeatsTitle: "Kindersitze", petTitle: "Tier",
    lug: { cabin: "Handgepäck", medium: "Mittelgross", large: "Gross", xl: "Extra gross", sports: "Sportausrüstung" },
    seats: { infant: "Babyschale", child: "Kindersitz", booster: "Sitzerhöhung" },
    pets: { none: "Kein Tier", noCarrier: "Ohne Box", withCarrier: "Mit Box" },
    flight: "Flugnummer", flightHint: "Falls Flughafen — wir warten kostenlos 45 min",
    notes: "Besondere Wünsche", corpTitle: "Firma / Hotel (optional)",
    company: "Firmenname", hotel: "Hotelname", guest: "Name des Gastes",
    poNumber: "Bestellnummer (PO)", billingRef: "Rechnungsreferenz",
    contactTitle: "Kontakt", name: "Vollständiger Name", email: "E-Mail", phone: "Telefon",
    breakdown: "Preisdetails",
    baseFare: "Grundgebühr", perKm: "Pro km", nightSup: "Nachtzuschlag", weekendSup: "Wochenendzuschlag",
    stopsSup: "Zwischenhalte", childSeatFee: "Kindersitze", petFee: "Tier",
    subtotal: "Zwischensumme", total: "Gesamt",
    distance: "Distanz", duration: "Fahrzeit", trafficDur: "Mit Verkehr", eta: "Ankunft",
    additionalAuth: "Ich autorisiere zusätzliche Belastungen für Wartezeit, Parking, Maut oder Extra-Halt gemäss AGB.",
    terms: "Ich akzeptiere die AGB und die Stornierungsbedingungen.",
    yourTrip: "Ihre Reise", singleCarNote: "Ein Fahrzeug, eine Chauffeurin.",
    conflictTitle: "Zeitfenster nicht verfügbar",
    conflictBody: "Bitte wählen Sie eine andere Uhrzeit oder klicken Sie auf «Nächsten freien Termin».",
    calculating: "Berechnung…", complete: "Fertig",
  },
  en: {
    steps: ["Route", "When", "Who & What", "Extras", "Summary"],
    step: "Step", of: "of",
    next: "Continue", back: "Back", pay: "Pay and confirm",
    servicePicker: "Choose your service",
    ride: "Point-to-point", byHour: "By the hour", multi: "Multi-stop", corp: "Company/Hotel", full: "Full day",
    hoursPack: "Hours package", pickHours: "Select hours",
    stopN: (n) => `Stop ${n}`, addStop: "Add stop", swap: "Swap",
    when: "When?", date: "Date", time: "Time", useNext: "Use next available slot",
    passengers: "Passengers", luggageTitle: "Luggage", childSeatsTitle: "Child seats", petTitle: "Pet",
    lug: { cabin: "Cabin", medium: "Medium", large: "Large", xl: "Extra large", sports: "Sports equipment" },
    seats: { infant: "Infant (0-1y)", child: "Child (1-3y)", booster: "Booster (3-12y)" },
    pets: { none: "No pet", noCarrier: "Without carrier", withCarrier: "With carrier" },
    flight: "Flight number", flightHint: "If airport — we wait 45 min at no charge",
    notes: "Special requests", corpTitle: "Company / Hotel (optional)",
    company: "Company name", hotel: "Hotel name", guest: "Guest name",
    poNumber: "PO number", billingRef: "Billing reference",
    contactTitle: "Contact", name: "Full name", email: "Email", phone: "Phone",
    breakdown: "Price breakdown",
    baseFare: "Base fare", perKm: "Per km", nightSup: "Night surcharge", weekendSup: "Weekend surcharge",
    stopsSup: "Intermediate stops", childSeatFee: "Child seats", petFee: "Pet",
    subtotal: "Subtotal", total: "Total",
    distance: "Distance", duration: "Duration", trafficDur: "With traffic", eta: "Arrival",
    additionalAuth: "I authorize additional charges for waiting time, parking, tolls or extra stops per T&Cs.",
    terms: "I accept the Terms & Conditions and cancellation policy.",
    yourTrip: "Your journey", singleCarNote: "One vehicle, one chauffeur.",
    conflictTitle: "Time slot unavailable",
    conflictBody: "Please choose another time or click «Use next available slot».",
    calculating: "Calculating…", complete: "Done",
  },
  pt: {
    steps: ["Rota", "Quando", "Quem & O quê", "Extras", "Resumo"],
    step: "Passo", of: "de",
    next: "Continuar", back: "Voltar", pay: "Pagar e confirmar",
    servicePicker: "Escolha o seu serviço",
    ride: "Ponto-a-ponto", byHour: "À hora", multi: "Multi-paragens", corp: "Empresa/Hotel", full: "Dia completo",
    hoursPack: "Pacote de horas", pickHours: "Selecione as horas",
    stopN: (n) => `Paragem ${n}`, addStop: "Adicionar paragem", swap: "Trocar",
    when: "Quando?", date: "Data", time: "Hora", useNext: "Usar próximo horário livre",
    passengers: "Passageiros", luggageTitle: "Bagagem", childSeatsTitle: "Cadeirinhas", petTitle: "Animal",
    lug: { cabin: "Cabine", medium: "Média", large: "Grande", xl: "Extra grande", sports: "Equipamento desportivo" },
    seats: { infant: "Ovinho (0-1a)", child: "Cadeirinha (1-3a)", booster: "Assento (3-12a)" },
    pets: { none: "Sem animal", noCarrier: "Sem transportadora", withCarrier: "Com transportadora" },
    flight: "Número do voo", flightHint: "Se aeroporto — esperamos 45 min sem custo",
    notes: "Pedidos especiais", corpTitle: "Empresa / Hotel (opcional)",
    company: "Nome da empresa", hotel: "Nome do hotel", guest: "Nome do hóspede",
    poNumber: "Número PO", billingRef: "Referência de facturação",
    contactTitle: "Contacto", name: "Nome completo", email: "Email", phone: "Telefone",
    breakdown: "Detalhe do preço",
    baseFare: "Taxa base", perKm: "Por km", nightSup: "Suplemento nocturno", weekendSup: "Suplemento fim-de-semana",
    stopsSup: "Paragens intermédias", childSeatFee: "Cadeirinhas", petFee: "Animal",
    subtotal: "Subtotal", total: "Total",
    distance: "Distância", duration: "Duração", trafficDur: "Com trânsito", eta: "Chegada",
    additionalAuth: "Autorizo cobranças adicionais por tempo de espera, estacionamento, portagens ou paragens extra conforme os T&C.",
    terms: "Aceito os Termos & Condições e política de cancelamento.",
    yourTrip: "A sua viagem", singleCarNote: "Um veículo, uma chauffeur.",
    conflictTitle: "Horário indisponível",
    conflictBody: "Escolha outra hora ou clique em «Usar próximo horário livre».",
    calculating: "A calcular…", complete: "Concluído",
  },
  fr: {
    steps: ["Trajet", "Quand", "Qui & Quoi", "Extras", "Résumé"],
    step: "Étape", of: "sur",
    next: "Continuer", back: "Retour", pay: "Payer et confirmer",
    servicePicker: "Choisissez votre service",
    ride: "Point-à-point", byHour: "À l'heure", multi: "Multi-arrêts", corp: "Entreprise/Hôtel", full: "Journée",
    hoursPack: "Forfait heures", pickHours: "Sélectionnez les heures",
    stopN: (n) => `Arrêt ${n}`, addStop: "Ajouter un arrêt", swap: "Inverser",
    when: "Quand ?", date: "Date", time: "Heure", useNext: "Utiliser le prochain créneau",
    passengers: "Passagers", luggageTitle: "Bagages", childSeatsTitle: "Sièges enfant", petTitle: "Animal",
    lug: { cabin: "Cabine", medium: "Moyen", large: "Grand", xl: "Très grand", sports: "Équipement sport" },
    seats: { infant: "Nourrisson (0-1a)", child: "Enfant (1-3a)", booster: "Rehausseur (3-12a)" },
    pets: { none: "Sans animal", noCarrier: "Sans caisse", withCarrier: "Avec caisse" },
    flight: "Numéro de vol", flightHint: "Si aéroport — 45 min d'attente offertes",
    notes: "Demandes spéciales", corpTitle: "Entreprise / Hôtel (optionnel)",
    company: "Nom entreprise", hotel: "Nom hôtel", guest: "Nom du client",
    poNumber: "Numéro PO", billingRef: "Réf. facturation",
    contactTitle: "Contact", name: "Nom complet", email: "Email", phone: "Téléphone",
    breakdown: "Détail du prix",
    baseFare: "Prise en charge", perKm: "Par km", nightSup: "Suppl. nuit", weekendSup: "Suppl. week-end",
    stopsSup: "Arrêts intermédiaires", childSeatFee: "Sièges enfant", petFee: "Animal",
    subtotal: "Sous-total", total: "Total",
    distance: "Distance", duration: "Durée", trafficDur: "Avec trafic", eta: "Arrivée",
    additionalAuth: "J'autorise des frais supplémentaires pour l'attente, le parking, les péages ou arrêts additionnels.",
    terms: "J'accepte les CGV et la politique d'annulation.",
    yourTrip: "Votre trajet", singleCarNote: "Un véhicule, une chauffeure.",
    conflictTitle: "Créneau indisponible",
    conflictBody: "Choisissez une autre heure ou cliquez sur « prochain créneau libre ».",
    calculating: "Calcul…", complete: "Terminé",
  },
  it: {
    steps: ["Percorso", "Quando", "Chi & Cosa", "Extra", "Riepilogo"],
    step: "Passo", of: "di",
    next: "Continua", back: "Indietro", pay: "Paga e conferma",
    servicePicker: "Scegli il tuo servizio",
    ride: "Punto-punto", byHour: "Ad ore", multi: "Multi-fermata", corp: "Azienda/Hotel", full: "Giornata",
    hoursPack: "Pacchetto ore", pickHours: "Seleziona ore",
    stopN: (n) => `Fermata ${n}`, addStop: "Aggiungi fermata", swap: "Inverti",
    when: "Quando?", date: "Data", time: "Ora", useNext: "Usa il prossimo orario libero",
    passengers: "Passeggeri", luggageTitle: "Bagagli", childSeatsTitle: "Seggiolini", petTitle: "Animale",
    lug: { cabin: "Cabina", medium: "Medio", large: "Grande", xl: "Extra grande", sports: "Attrezzatura sport" },
    seats: { infant: "Neonato (0-1a)", child: "Bambino (1-3a)", booster: "Rialzo (3-12a)" },
    pets: { none: "Nessuno", noCarrier: "Senza trasportino", withCarrier: "Con trasportino" },
    flight: "Numero volo", flightHint: "Se aeroporto — 45 min di attesa gratuita",
    notes: "Richieste speciali", corpTitle: "Azienda / Hotel (opzionale)",
    company: "Nome azienda", hotel: "Nome hotel", guest: "Nome ospite",
    poNumber: "Numero PO", billingRef: "Rif. fatturazione",
    contactTitle: "Contatto", name: "Nome completo", email: "Email", phone: "Telefono",
    breakdown: "Dettaglio prezzo",
    baseFare: "Tariffa base", perKm: "Per km", nightSup: "Suppl. notturno", weekendSup: "Suppl. weekend",
    stopsSup: "Fermate intermedie", childSeatFee: "Seggiolini", petFee: "Animale",
    subtotal: "Subtotale", total: "Totale",
    distance: "Distanza", duration: "Durata", trafficDur: "Con traffico", eta: "Arrivo",
    additionalAuth: "Autorizzo addebiti aggiuntivi per attesa, parcheggio, pedaggi o fermate extra.",
    terms: "Accetto Termini e Condizioni e politica di cancellazione.",
    yourTrip: "Il tuo viaggio", singleCarNote: "Un veicolo, una chauffeur.",
    conflictTitle: "Orario non disponibile",
    conflictBody: "Scegli un altro orario o clicca su «prossimo orario libero».",
    calculating: "Calcolo…", complete: "Fatto",
  },
};

const getUi = (lang) => UI[lang] || UI.de;

// --- Day-picker (iCloud availability) copy — additive, per language --------
const DAYPICKER_COPY = {
  de: {
    title: "Verfügbarkeit",
    subtitle: "Freie Tage · gemäss Agenda der Chauffeurin",
    unavailable: "Nicht verfügbar",
    busyOn: "Belegt am",
    partial: "Teilweise belegt",
    full: "Ganztägig belegt",
    free: "Frei",
    slotUnavailable: "Dieses Zeitfenster ist im Hauptkalender bereits belegt. Bitte wählen Sie einen anderen Tag oder eine andere Uhrzeit.",
    weekdays: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    months: ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"],
  },
  en: {
    title: "Availability",
    subtitle: "Open days · matched against the chauffeur's main calendar",
    unavailable: "Unavailable",
    busyOn: "Busy on",
    partial: "Partially busy",
    full: "Fully booked",
    free: "Free",
    slotUnavailable: "This time slot is already blocked in the main calendar. Please pick another day or time.",
    weekdays: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"],
    months: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
  },
  pt: {
    title: "Disponibilidade",
    subtitle: "Dias livres · sincronizados com a agenda da chauffeur",
    unavailable: "Indisponível",
    busyOn: "Ocupado em",
    partial: "Parcialmente ocupado",
    full: "Totalmente ocupado",
    free: "Livre",
    slotUnavailable: "Este horário já está ocupado na agenda principal. Escolha outro dia ou hora.",
    weekdays: ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"],
    months: ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"],
  },
  fr: {
    title: "Disponibilité",
    subtitle: "Jours libres · d'après l'agenda de la chauffeure",
    unavailable: "Indisponible",
    busyOn: "Occupé le",
    partial: "Partiellement occupé",
    full: "Complet",
    free: "Libre",
    slotUnavailable: "Ce créneau est déjà bloqué dans l'agenda principale. Choisissez un autre jour ou une autre heure.",
    weekdays: ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"],
    months: ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"],
  },
  it: {
    title: "Disponibilità",
    subtitle: "Giorni liberi · sincronizzati con l'agenda della chauffeur",
    unavailable: "Non disponibile",
    busyOn: "Occupato il",
    partial: "Parzialmente occupato",
    full: "Tutto prenotato",
    free: "Libero",
    slotUnavailable: "Questo orario è già bloccato nell'agenda principale. Scegli un altro giorno o orario.",
    weekdays: ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"],
    months: ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"],
  },
};
const getDayCopy = (lang) => DAYPICKER_COPY[lang] || DAYPICKER_COPY.en;

// Return true if [aStart,aEnd) overlaps [bStart,bEnd)
const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aEnd > bStart && aStart < bEnd;

// Format HH:MM in Europe/Zurich for a UTC ISO string
const zurichTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("de-CH", {
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich",
    });
  } catch { return ""; }
};

// Given the day (local Zurich Y-M-D key), return list of busy intervals overlapping.
const busyForDayKey = (icloudBusy, dayStart, dayEnd) =>
  (icloudBusy || []).filter((iv) => {
    const s = new Date(iv.start_utc).getTime();
    const e = new Date(iv.end_utc).getTime();
    return rangesOverlap(s, e, dayStart.getTime(), dayEnd.getTime());
  });

// Get the Y-M-D bounds for a given day in Zurich local time (returned as JS Date UTC-anchored)
const zurichDayBounds = (baseDate) => {
  // Compute the Zurich Y-M-D of baseDate
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit",
  });
  const [y, m, d] = fmt.format(baseDate).split("-").map(Number);
  // Build 00:00 and 24:00 Zurich → convert to UTC via a probe
  // Easiest: use a Date at that Y-M-D noon-UTC, then subtract the Zurich offset
  // We can compute the offset by comparing formatted zurich time to base.
  const start = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  // Fine-tune: adjust for tz offset by rendering "hour" of start in Zurich
  const zHourStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Zurich", hour: "2-digit", hour12: false,
  }).format(start);
  const zHour = parseInt(zHourStr, 10) || 0;
  // If Zurich hour at UTC midnight is `zHour`, then Zurich midnight is at UTC time (24 - zHour) hours earlier
  const startUtc = new Date(start.getTime() - zHour * 3600 * 1000);
  const endUtc = new Date(startUtc.getTime() + 24 * 3600 * 1000);
  return { startUtc, endUtc, key: `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`, y, m, d };
};

// --- Main component --------------------------------------------------------
const Reservar = () => {
  const { lang } = useLang();
  const bk = getBooking(lang);
  const u = getUi(lang);
  const q = useQuery();
  const scrollTop = useRef(null);

  const initialWhen = q.get("when") ? isoLocal(q.get("when")) : nowLocalIso();

  const [step, setStep] = useState(0); // 0..4
  const [form, setForm] = useState({
    service: q.get("service") || "transfer", // transfer | by_hour | multicity | corporate | full_day
    origin: q.get("origin") || "",
    origin_coords: null,
    destination: q.get("destination") || "",
    destination_coords: null,
    stops: [], // multicity
    hours: 4, // by-hour package
    pickup: initialWhen,
    passengers: 1,
    luggage: { cabin: 0, medium: 0, large: 0, xl: 0, sports: 0 },
    child_seats: { infant: 0, child: 0, booster: 0 },
    pet: "none",
    flight_number: "",
    notes: "",
    company_name: "", hotel_name: "", guest_name: "", po_number: "", billing_reference: "",
    name: "", email: "", phone: "",
    terms_accepted: false, additional_charges_authorized: false,
  });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState([]);
  const [icloudBusy, setIcloudBusy] = useState([]);
  const [nextSlot, setNextSlot] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [dispatch, setDispatch] = useState(null); // {available, earliest_possible_pickup, chauffeur_position, ...}
  const [altSlots, setAltSlots] = useState([]);   // alternative slots array
  const [loadingAlts, setLoadingAlts] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Preferred Partner Benefit — checkout-time code input + applied result.
  const [partnerCode, setPartnerCode] = useState("");
  const [partnerApplying, setPartnerApplying] = useState(false);
  const [partnerBenefit, setPartnerBenefit] = useState(null); // { public_price_chf, benefit_amount_chf, final_price_chf, partner_name, description }
  const [partnerError, setPartnerError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDeep = (k, sk, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [sk]: v } }));

  // Apply / remove a partner benefit code against the current quote.
  const applyPartner = async () => {
    setPartnerError(null);
    const code = partnerCode.trim().toUpperCase();
    if (!code) return;
    const subtotal = quote?.swissalpenline?.total_chf;
    if (!subtotal) { setPartnerError("Compute your quote first"); return; }
    setPartnerApplying(true);
    try {
      const { data } = await axios.post(`${API}/vouchers/apply`, {
        code, subtotal_chf: subtotal,
      });
      setPartnerBenefit(data);
    } catch (e) {
      setPartnerError(e?.response?.data?.detail || "Invalid partner code");
      setPartnerBenefit(null);
    }
    setPartnerApplying(false);
  };
  const clearPartner = () => { setPartnerBenefit(null); setPartnerCode(""); setPartnerError(null); };

  const swap = () => setForm((f) => ({
    ...f, origin: f.destination, destination: f.origin,
    origin_coords: f.destination_coords, destination_coords: f.origin_coords,
  }));

  const addStop = () => setForm((f) => ({
    ...f, stops: [...f.stops, { _id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, address: "", coords: null }],
  }));
  const removeStop = (i) => setForm((f) => ({ ...f, stops: f.stops.filter((_, ix) => ix !== i) }));
  const setStop = (i, address, coords) => setForm((f) => {
    const s = [...f.stops]; s[i] = { ...s[i], address, coords }; return { ...f, stops: s };
  });

  // Availability + next-slot on mount. iCloud busy intervals are also
  // refetched on window focus / tab visibility / every 60 s while the page
  // is open, so a fresh event Paula creates on her iPhone becomes visible
  // in the day-picker within a minute even without a full page reload.
  useEffect(() => {
    let cancelled = false;
    const fetchAll = () => {
      axios.get(`${API}/availability?days=60`)
        .then(({ data }) => { if (!cancelled) setBusy(data.busy || []); })
        .catch(() => {});
      axios.get(`${API}/availability/next-slot?duration_min=90`)
        .then(({ data }) => { if (!cancelled) setNextSlot(data.next_slot); })
        .catch(() => {});
      axios.get(`${API}/calendar/busy-intervals?days=60`)
        .then(({ data }) => { if (!cancelled) setIcloudBusy(data.intervals || []); })
        .catch(() => { if (!cancelled) setIcloudBusy([]); });
    };

    fetchAll();

    const refreshOnActive = () => {
      if (document.visibilityState === "visible") fetchAll();
    };
    window.addEventListener("focus", fetchAll);
    document.addEventListener("visibilitychange", refreshOnActive);
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") fetchAll();
    }, 60_000);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", fetchAll);
      document.removeEventListener("visibilitychange", refreshOnActive);
      clearInterval(poll);
    };
  }, []);

  const totalChildSeats = form.child_seats.infant + form.child_seats.child + form.child_seats.booster;

  // Live quote whenever inputs change
  useEffect(() => {
    const s = form.service;
    if (s === "full_day") { setQuote(null); return; }

    if (s === "by_hour") {
      const t = setTimeout(async () => {
        setQuoting(true);
        try {
          const { data } = await axios.post(`${API}/quote/by-hour`, {
            hours: Number(form.hours) || 4,
            when: new Date(form.pickup).toISOString(),
            origin: form.origin || null,
          });
          setQuote({ swissalpenline: data, byHour: data, distance_km: null, duration_min: (Number(form.hours) || 4) * 60 });
        } catch { setQuote(null); } finally { setQuoting(false); }
      }, 350);
      return () => clearTimeout(t);
    }

    if (s === "multicity") {
      const stopsList = form.stops.filter((st) => st.address);
      if (!form.origin || !form.destination || stopsList.length === 0) { setQuote(null); return; }
      const t = setTimeout(async () => {
        setQuoting(true);
        try {
          const pts = [form.origin, ...stopsList.map((st) => st.address), form.destination];
          const { data } = await axios.post(`${API}/quote/multicity`, {
            stops: pts, when: new Date(form.pickup).toISOString(),
            child_seats: totalChildSeats, pet: form.pet,
          });
          setQuote(data);
        } catch { setQuote(null); } finally { setQuoting(false); }
      }, 500);
      return () => clearTimeout(t);
    }

    if (!form.origin || !form.destination || !form.pickup) { setQuote(null); return; }
    const t = setTimeout(async () => {
      setQuoting(true);
      try {
        const { data } = await axios.post(`${API}/quote`, {
          origin: form.origin, destination: form.destination,
          when: new Date(form.pickup).toISOString(),
          service: s === "corporate" ? "transfer" : s,
          child_seats: totalChildSeats, pet: form.pet,
        });
        setQuote(data);
      } catch { setQuote(null); } finally { setQuoting(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [form.origin, form.destination, form.pickup, form.service, totalChildSeats, form.pet, form.hours, JSON.stringify(form.stops)]);

  // Conflict + smart dispatch analysis
  useEffect(() => {
    if (!form.pickup || !form.origin) { setConflict(null); setDispatch(null); return; }
    if (form.service === "full_day" || form.service === "by_hour") { setConflict(null); setDispatch(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/dispatch/analyze`, {
          origin: form.origin,
          destination: form.destination || null,
          pickup_datetime: new Date(form.pickup).toISOString(),
          service: form.service === "corporate" ? "transfer" : form.service,
        });
        setDispatch(data);
        setConflict(data.available ? null : data);
      } catch {
        setDispatch(null); setConflict(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form.pickup, form.origin, form.destination, form.service]);

  const fetchAlternativeSlots = async () => {
    if (!form.origin || !form.pickup) return;
    setLoadingAlts(true);
    try {
      const { data } = await axios.post(`${API}/dispatch/suggest-times`, {
        origin: form.origin,
        destination: form.destination || null,
        around_datetime: new Date(form.pickup).toISOString(),
        count: 4,
      });
      setAltSlots(data.slots || []);
    } catch { setAltSlots([]); }
    setLoadingAlts(false);
  };

  const acceptEarliest = () => {
    if (!dispatch?.earliest_possible_pickup) return;
    set("pickup", isoLocal(dispatch.earliest_possible_pickup));
    setAltSlots([]);
  };

  const goto = (n) => {
    setStep(n);
    if (scrollTop.current) scrollTop.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const next = () => step < 4 && goto(step + 1);
  const prev = () => step > 0 && goto(step - 1);

  // iCloud (main calendar) — derived conflict on current pickup + estimated duration.
  const iCloudConflict = useMemo(() => {
    if (!form.pickup || !icloudBusy?.length) return null;
    const pStart = new Date(form.pickup);
    if (Number.isNaN(pStart.getTime())) return null;
    // Estimate duration: quote > by_hour/full_day service > default 90 min
    let durMin = 90;
    if (quote?.duration_in_traffic_min) durMin = quote.duration_in_traffic_min;
    else if (quote?.duration_min) durMin = quote.duration_min;
    else if (form.service === "by_hour" || form.service === "full_day") durMin = (Number(form.hours) || 4) * 60;
    const pEnd = new Date(pStart.getTime() + durMin * 60 * 1000);
    for (const iv of icloudBusy) {
      const s = new Date(iv.start_utc);
      const e = new Date(iv.end_utc);
      if (rangesOverlap(pStart, pEnd, s, e)) {
        return { start_utc: iv.start_utc, end_utc: iv.end_utc, ours: !!iv.ours };
      }
    }
    return null;
  }, [form.pickup, form.service, form.hours, icloudBusy, quote?.duration_min, quote?.duration_in_traffic_min]);

  // Day-picker: build the next 14 days (Zurich local) with a status per day.
  const dayCopy = getDayCopy(lang);
  const dayCards = useMemo(() => {
    const cards = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const base = new Date(now.getTime() + i * 24 * 3600 * 1000);
      const { startUtc, endUtc, key, y, m, d } = zurichDayBounds(base);
      const intervals = busyForDayKey(icloudBusy, startUtc, endUtc);
      // Compute how many "business hours" (09:00-19:30 Zurich = 10.5h) are covered
      let coveredMin = 0;
      for (const iv of intervals) {
        const s = Math.max(new Date(iv.start_utc).getTime(), startUtc.getTime());
        const e = Math.min(new Date(iv.end_utc).getTime(), endUtc.getTime());
        if (e > s) coveredMin += (e - s) / 60000;
      }
      // Categorize:
      // - full: covered >= 10 hours OR any interval spans the whole day
      // - partial: at least one interval
      // - free: no intervals
      const isFull = coveredMin >= 600 || intervals.some((iv) => {
        const s = new Date(iv.start_utc).getTime();
        const e = new Date(iv.end_utc).getTime();
        return s <= startUtc.getTime() && e >= endUtc.getTime();
      });
      const status = intervals.length === 0 ? "free" : (isFull ? "full" : "partial");
      cards.push({ key, y, m, d, weekday: base.getDay(), status, intervals });
    }
    return cards;
  }, [icloudBusy]);

  // Business rules — for the pickup default when user clicks a free day.
  const [businessRules, setBusinessRules] = useState({ business_hours_start: "09:00" });
  useEffect(() => {
    axios.get(`${API}/business-rules`)
      .then(({ data }) => setBusinessRules({ business_hours_start: data.business_hours_start || "09:00" }))
      .catch(() => {});
  }, []);

  const pickDay = (card) => {
    if (card.status === "full") return; // hard-block
    // Default the pickup to the start of business hours (admin-configurable
    // via /admin/business-rules → `business_hours_start`).
    const start = (businessRules.business_hours_start || "09:00").slice(0, 5);
    const iso = `${card.key}T${start}`;
    set("pickup", iso);
  };

  // Step validation
  const canProceed = () => {
    if (step === 0) {
      if (form.service === "by_hour") return !!(form.origin);
      return !!(form.origin && form.destination);
    }
    if (step === 1) return !!form.pickup && !conflict && !iCloudConflict && !(quote?.policy && quote.policy.accepted === false);
    if (step === 2) return form.passengers >= 1;
    if (step === 3) return true;
    return true;
  };

  const submit = async () => {
    if (!form.terms_accepted) { toast.error(bk.errors.terms); return; }
    if (!form.name || !form.email || !form.phone) { toast.error(bk.errors.required); return; }
    setSubmitting(true);
    try {
      const cleanedStops = form.stops.filter((s) => s.address).map((s) => ({ address: s.address, coords: s.coords }));
      const payload = {
        service: form.service === "corporate" ? "transfer" : form.service,
        origin: form.origin || null,
        destination: form.destination || null,
        origin_coords: form.origin_coords,
        destination_coords: form.destination_coords,
        stops: form.service === "multicity" ? cleanedStops : null,
        hours: (form.service === "by_hour" || form.service === "full_day") ? Number(form.hours) : null,
        pickup_datetime: new Date(form.pickup).toISOString(),
        passengers: Number(form.passengers) || 1,
        child_seats: form.child_seats,
        pet: form.pet,
        luggage: form.luggage,
        flight_number: form.flight_number || null,
        notes: form.notes || null,
        company_name: form.company_name || null,
        hotel_name: form.hotel_name || null,
        guest_name: form.guest_name || null,
        po_number: form.po_number || null,
        billing_reference: form.billing_reference || null,
        contact_name: form.name, contact_email: form.email, contact_phone: form.phone,
        terms_accepted: true, additional_charges_authorized: form.additional_charges_authorized,
        voucher_code: partnerBenefit?.code || null,
        quote_snapshot: quote ? {
          total_chf: partnerBenefit?.final_price_chf ?? (quote.swissalpenline?.total_chf || 0),
          public_price_chf: quote.swissalpenline?.total_chf || 0,
          partner_benefit_chf: partnerBenefit?.benefit_amount_chf || 0,
          partner_code: partnerBenefit?.code || null,
          distance_km: quote.distance_km, duration_min: quote.duration_min,
          duration_in_traffic_min: quote.duration_in_traffic_min,
          hour_tier: quote.swissalpenline?.hour_tier,
        } : null,
      };
      const { data: r } = await axios.post(`${API}/reservations`, payload);
      const { data: checkout } = await axios.post(`${API}/checkout/session`, {
        reservation_id: r.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "slot_unavailable") {
        toast.error(bk.errors.conflict); goto(1);
      } else if (["winter_altitude", "after_hours_min", "closure_day"].includes(detail?.code)) {
        toast.error(detail.message || bk.errors.generic);
        goto(1);
      } else if (detail?.code === "calendar_conflict") {
        toast.error(detail.message || dayCopy.slotUnavailable);
        goto(1);
      } else {
        toast.error(typeof detail === "string" ? detail : bk.errors.generic);
      }
      setSubmitting(false);
    }
  };

  const totalCHF = quote?.swissalpenline?.total_chf;
  const distanceKm = quote?.distance_km;
  const durationMin = quote?.duration_min;
  const trafficMin = quote?.duration_in_traffic_min;
  const etaIso = quote?.eta_iso;

  // ---- STEP RENDER --------------------------------------------------------
  const stepIndicator = (
    <div className="flex items-center gap-2 sm:gap-4 mb-10">
      {u.steps.map((label, i) => {
        const done = i < step; const active = i === step;
        return (
          <button key={label} type="button" onClick={() => done && goto(i)}
                  data-testid={`step-dot-${i}`}
                  className={`group flex items-center gap-2 flex-1 min-w-0 ${done ? "cursor-pointer" : "cursor-default"}`}>
            <span className={`w-8 h-8 shrink-0 border flex items-center justify-center text-[10px] tabular-nums transition-all duration-500 ${
              active ? "border-bronze bg-bronze text-brand-bg" :
              done ? "border-bronze text-bronze" : "border-brand-border text-brand-dim"
            }`}>{done ? <Check size={12} /> : i + 1}</span>
            <span className={`text-[10px] tracking-widest2 uppercase truncate transition-colors ${
              active ? "text-brand-platinum" : done ? "text-brand-silver" : "text-brand-dim"
            }`}>{label}</span>
            {i < u.steps.length - 1 && <span className="hidden sm:block flex-1 h-px bg-brand-border/40" />}
          </button>
        );
      })}
    </div>
  );

  const services = [
    { v: "transfer", label: u.ride, icon: RouteIcon },
    { v: "by_hour", label: u.byHour, icon: Clock },
    { v: "multicity", label: u.multi, icon: MapPin },
    { v: "corporate", label: u.corp, icon: Building2 },
    { v: "full_day", label: u.full, icon: CarFront },
  ];

  const showRoute = form.service !== "by_hour" && form.service !== "full_day";

  return (
    <div className="pt-32 pb-24 min-h-screen bg-brand-bg">
      <div ref={scrollTop} className="max-w-[1400px] mx-auto px-4 lg:px-6">

        {/* HERO */}
        <div className="mb-10 fadeUp">
          <p className="text-[10px] tracking-widest2 uppercase text-bronze mb-3">— {u.step} {step + 1} {u.of} 5</p>
          <h1 className="display text-5xl lg:text-7xl text-brand-platinum leading-[1.05] mb-4">{u.steps[step]}.</h1>
          <p className="text-sm text-brand-silver max-w-lg">{bk.subtitle}</p>
        </div>

        {stepIndicator}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] gap-8 lg:gap-12 items-start">

          {/* ============================================================ */}
          {/* LEFT: STEP CONTENT                                            */}
          {/* ============================================================ */}
          <div className="min-h-[500px]" key={step}>

            {/* STEP 0 — ROUTE ------------------------------------------- */}
            {step === 0 && (
              <div className="fadeUp space-y-8">
                <div>
                  <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {u.servicePicker}</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {services.map((s) => (
                      <Chip key={s.v} active={form.service === s.v} onClick={() => set("service", s.v)} testId={`service-${s.v}`}>
                        <s.icon size={14} className="inline mr-2 -mt-0.5" />
                        {s.label}
                      </Chip>
                    ))}
                  </div>
                </div>

                {form.service === "by_hour" && (
                  <div>
                    <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {u.hoursPack}</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[2, 3, 4, 5, 6, 8, 10, 12].map((h) => (
                        <Chip key={h} active={form.hours === h} onClick={() => set("hours", h)} testId={`hour-pack-${h}`}>
                          {h} h
                        </Chip>
                      ))}
                    </div>
                    <p className="text-xs text-brand-dim mt-3">
                      Minimum booking 2 h · above 12 h consider Executive Full Day or Multi-Day chauffeur.
                    </p>
                  </div>
                )}

                {form.service !== "full_day" && (
                  <div className="relative">
                    <div className="absolute left-3 top-8 bottom-8 w-px bg-brand-border/60" />
                    <div className="space-y-4">
                      <div className="relative flex items-start gap-4">
                        <span className="mt-3 h-3 w-3 rounded-full bg-bronze shrink-0 z-10" />
                        <label className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">
                              {form.service === "by_hour" ? bk.pickup : bk.services.transferDesc.split(" ")[0]}
                            </span>
                            <CurrentLocationButton
                              onLocation={(l, c) => setForm((f) => ({ ...f, origin: l, origin_coords: c }))}
                              label={bk.current} />
                          </div>
                          <AddressAutocomplete value={form.origin}
                            onChange={(l, c) => setForm((f) => ({ ...f, origin: l, origin_coords: c }))}
                            placeholder={bk.addressPh} testId={TID.book.origin}
                            className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 text-lg focus:border-bronze transition-colors" />
                        </label>
                      </div>

                      {form.service === "multicity" && form.stops.map((st, i) => (
                        <div key={st._id} className="relative flex items-start gap-4">
                          <span className="mt-3 h-3 w-3 rounded-full bg-bronze/60 shrink-0 z-10" />
                          <label className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] tracking-widest2 uppercase text-brand-silver">{u.stopN(i + 1)}</span>
                              <button type="button" onClick={() => removeStop(i)}
                                      data-testid={`book-stop-remove-${i}`}
                                      className="text-brand-silver hover:text-red-400 transition-colors"><X size={14} /></button>
                            </div>
                            <AddressAutocomplete value={st.address}
                              onChange={(l, c) => setStop(i, l, c)}
                              placeholder={bk.addressPh} testId={`book-stop-${i}`}
                              className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 text-lg focus:border-bronze transition-colors" />
                          </label>
                        </div>
                      ))}

                      {form.service !== "by_hour" && (
                        <div className="relative flex items-start gap-4">
                          <span className="mt-3 h-3 w-3 rotate-45 bg-brand-platinum shrink-0 z-10" />
                          <label className="flex-1">
                            <span className="block text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">
                              {bk.services.transferDesc.split(" ").slice(-1)[0]}
                            </span>
                            <AddressAutocomplete value={form.destination}
                              onChange={(l, c) => setForm((f) => ({ ...f, destination: l, destination_coords: c }))}
                              placeholder={bk.addressPh} testId={TID.book.destination}
                              className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 text-lg focus:border-bronze transition-colors" />
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-6 pl-7">
                      {form.service === "multicity" ? (
                        <button type="button" onClick={addStop} data-testid="book-add-stop"
                                className="text-bronze hover:text-brand-platinum text-[10px] tracking-widest2 uppercase flex items-center gap-1.5 transition-colors">
                          <Plus size={12} /> {u.addStop}
                        </button>
                      ) : <span />}
                      {form.service !== "by_hour" && (
                        <button type="button" onClick={swap} data-testid={TID.calc.swap}
                                className="text-brand-silver hover:text-brand-platinum text-[10px] tracking-widest2 uppercase flex items-center gap-1.5 transition-colors">
                          <ArrowRightLeft size={12} /> {u.swap}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {form.service === "full_day" && (
                  <div>
                    <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {u.pickHours}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[8, 10, 12].map((h) => (
                        <Chip key={h} active={form.hours === h} onClick={() => set("hours", h)} testId={`fd-hours-${h}`}>{h} h</Chip>
                      ))}
                    </div>
                    <p className="text-xs text-brand-dim mt-3">{bk.services.fullDayDesc}</p>
                  </div>
                )}
              </div>
            )}

            {/* STEP 1 — WHEN -------------------------------------------- */}
            {step === 1 && (
              <div className="fadeUp space-y-8">
                {nextSlot && (
                  <button type="button" onClick={() => set("pickup", isoLocal(nextSlot))}
                          data-testid="next-slot-card"
                          className="w-full group flex items-center justify-between gap-4 border border-bronze/50 bg-bronze/[0.06] hover:bg-bronze/[0.12] px-6 py-5 transition-all">
                    <div className="flex items-center gap-4 text-left">
                      <Sparkles size={18} className="text-bronze shrink-0" />
                      <div>
                        <p className="text-[10px] tracking-widest2 uppercase text-bronze mb-1">{u.useNext}</p>
                        <p className="text-brand-platinum text-lg font-light">
                          {new Date(nextSlot).toLocaleString(lang, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-brand-silver group-hover:text-brand-platinum" />
                  </button>
                )}

                <div data-testid="icloud-day-picker">
                  <div className="flex items-baseline justify-between mb-3">
                    <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">— {dayCopy.title}</p>
                    <p className="text-[10px] text-brand-dim">{dayCopy.subtitle}</p>
                  </div>
                  <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                    {dayCards.map((c) => {
                      const isSelected = form.pickup && form.pickup.startsWith(c.key);
                      const label = `${dayCopy.weekdays[c.weekday]} ${c.d} ${dayCopy.months[c.m - 1]}`;
                      const tooltipLines =
                        c.status === "free"
                          ? dayCopy.free
                          : c.intervals
                              .map((iv) => `${zurichTime(iv.start_utc)}–${zurichTime(iv.end_utc)}${iv.ours ? " (SwissAlpenLine)" : ""}`)
                              .join("\n");
                      const title = c.status === "full"
                        ? `${dayCopy.unavailable}\n${dayCopy.busyOn} ${label}\n${tooltipLines}`
                        : c.status === "partial"
                        ? `${dayCopy.partial}\n${dayCopy.busyOn} ${label}\n${tooltipLines}`
                        : `${dayCopy.free} · ${label}`;
                      const stateCls =
                        c.status === "full"
                          ? "border-red-500/30 bg-red-500/[0.04] text-brand-dim opacity-40 cursor-not-allowed line-through"
                          : c.status === "partial"
                          ? "border-bronze/40 bg-bronze/[0.05] text-brand-platinum hover:border-bronze cursor-pointer"
                          : "border-brand-border text-brand-platinum hover:border-bronze cursor-pointer";
                      const selectedCls = isSelected ? "!border-bronze bg-bronze/[0.12]" : "";
                      return (
                        <button
                          key={c.key}
                          type="button"
                          disabled={c.status === "full"}
                          onClick={() => pickDay(c)}
                          title={title}
                          aria-label={title}
                          aria-disabled={c.status === "full"}
                          data-testid={`day-cell-${c.key}`}
                          data-day-status={c.status}
                          className={`relative border p-2 sm:p-3 transition-all text-center ${stateCls} ${selectedCls}`}
                        >
                          <div className="text-[9px] tracking-widest2 uppercase text-brand-dim mb-0.5">
                            {dayCopy.weekdays[c.weekday]}
                          </div>
                          <div className="text-base sm:text-lg tabular-nums leading-none">{c.d}</div>
                          <div className="text-[9px] text-brand-dim mt-0.5">{dayCopy.months[c.m - 1]}</div>
                          {c.status === "partial" && (
                            <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-bronze" />
                          )}
                          {c.status === "full" && (
                            <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-red-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* Legend */}
                  <div className="mt-3 flex items-center gap-4 text-[10px] tracking-widest2 uppercase text-brand-dim">
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-brand-platinum" />{dayCopy.free}</span>
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-bronze" />{dayCopy.partial}</span>
                    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-red-500" />{dayCopy.full}</span>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {u.when}</p>
                  <input type="datetime-local" value={form.pickup} onChange={(e) => set("pickup", e.target.value)}
                         data-testid={TID.book.datetime}
                         className={`w-full bg-transparent border-b text-brand-platinum py-4 text-2xl focus:outline-none transition-colors ${
                           conflict || iCloudConflict ? "border-red-500" : "border-brand-border focus:border-bronze"
                         }`} />
                </div>

                {iCloudConflict && (
                  <div className="border border-red-500/50 bg-red-500/[0.05] p-6 fadeUp space-y-3" data-testid="icloud-conflict-warning">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={18} className="text-red-400 shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-brand-platinum mb-2">{dayCopy.unavailable}</p>
                        <p className="text-xs text-brand-silver leading-relaxed">
                          {dayCopy.slotUnavailable}
                        </p>
                        <p className="text-[11px] text-brand-dim mt-3">
                          {dayCopy.busyOn} {zurichTime(iCloudConflict.start_utc)}–{zurichTime(iCloudConflict.end_utc)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {conflict && dispatch && !dispatch.available && (
                  <div className="border border-bronze/50 bg-bronze/[0.06] p-6 fadeUp space-y-5" data-testid="conflict-warning">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={18} className="text-bronze shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-brand-platinum mb-2">{u.conflictTitle}</p>
                        <p className="text-xs text-brand-silver leading-relaxed">
                          {dispatch.message || u.conflictBody}
                        </p>
                        {dispatch.chauffeur_position && (
                          <p className="text-[11px] text-brand-dim mt-3">
                            <MapPin size={11} className="inline mr-1" />
                            {dispatch.chauffeur_position.source === "active_booking" && "En route · "}
                            {dispatch.chauffeur_position.source === "prev_booking" && "Last drop-off · "}
                            {dispatch.chauffeur_position.address}
                            {dispatch.reposition_min > 0 && ` · +${dispatch.reposition_min} min repositioning`}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button type="button" onClick={acceptEarliest}
                              data-testid="dispatch-accept-earliest"
                              className="btn-pill btn-pill--solid justify-center">
                        <Check size={14} />
                        {new Date(dispatch.earliest_possible_pickup).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" })}
                        <ArrowRight size={14} />
                      </button>
                      <button type="button" onClick={fetchAlternativeSlots}
                              data-testid="dispatch-choose-other"
                              className="btn-pill justify-center">
                        {loadingAlts ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                        Choose another time
                      </button>
                    </div>

                    {altSlots.length > 0 && (
                      <div className="pt-4 border-t border-brand-border/60">
                        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-3">— Available times</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {altSlots.map((iso, i) => (
                            <button key={iso} type="button"
                                    onClick={() => { set("pickup", isoLocal(iso)); setAltSlots([]); }}
                                    data-testid={`alt-slot-${i}`}
                                    className="border border-brand-border hover:border-bronze bg-brand-bg py-3 text-brand-platinum text-sm transition-all">
                              {new Date(iso).toLocaleString(lang, { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* STEP 2 — WHO & WHAT ------------------------------------- */}
            {step === 2 && (
              <div className="fadeUp space-y-2">
                <StepRow label={u.passengers} right={
                  <Counter value={form.passengers} min={1} max={4}
                           onChange={(v) => set("passengers", v)} testId="pax" />
                }>
                  <p className="text-xs text-brand-dim"><Users size={11} className="inline mr-1" /> BYD Seal U · 4 max</p>
                </StepRow>

                <StepRow label={u.luggageTitle}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {Object.entries(u.lug).map(([k, label]) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="text-sm text-brand-silver flex items-center gap-2"><Luggage size={12} /> {label}</span>
                        <Counter value={form.luggage[k]} onChange={(v) => setDeep("luggage", k, v)} testId={`lug-${k}`} />
                      </div>
                    ))}
                  </div>
                </StepRow>

                <StepRow label={u.childSeatsTitle}>
                  <div className="space-y-3">
                    {Object.entries(u.seats).map(([k, label]) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="text-sm text-brand-silver flex items-center gap-2"><Baby size={12} /> {label}</span>
                        <Counter value={form.child_seats[k]} onChange={(v) => setDeep("child_seats", k, v)} testId={`seat-${k}`} />
                      </div>
                    ))}
                  </div>
                </StepRow>

                <StepRow label={u.petTitle}>
                  <div className="flex gap-2">
                    {Object.entries(u.pets).map(([k, label]) => (
                      <Chip key={k}
                            active={(k === "none" && form.pet === "none") ||
                                    (k === "noCarrier" && form.pet === "no_carrier") ||
                                    (k === "withCarrier" && form.pet === "with_carrier")}
                            onClick={() => set("pet", k === "none" ? "none" : k === "noCarrier" ? "no_carrier" : "with_carrier")}
                            testId={`pet-${k}`}>
                        <Dog size={12} className="inline mr-2 -mt-0.5" />{label}
                      </Chip>
                    ))}
                  </div>
                </StepRow>
              </div>
            )}

            {/* STEP 3 — EXTRAS ----------------------------------------- */}
            {step === 3 && (
              <div className="fadeUp space-y-2">
                {(isAirport(form.origin) || isAirport(form.destination)) && (
                  <StepRow label={u.flight} sublabel={u.flightHint}>
                    <input value={form.flight_number}
                           onChange={(e) => set("flight_number", e.target.value.toUpperCase())}
                           placeholder="LX 1234" data-testid="book-flight"
                           className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 text-lg uppercase tracking-widest2 focus:border-bronze transition-colors" />
                  </StepRow>
                )}

                <StepRow label={u.notes}>
                  <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                            placeholder={bk.notesPh} data-testid={TID.book.notes}
                            className="w-full bg-transparent border border-brand-border text-brand-platinum p-4 text-sm resize-none focus:border-bronze transition-colors" />
                </StepRow>

                <StepRow label={u.corpTitle}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { k: "company_name", ph: u.company, icon: Building2 },
                      { k: "hotel_name", ph: u.hotel, icon: Building2 },
                      { k: "guest_name", ph: u.guest, icon: Users },
                      { k: "po_number", ph: u.poNumber, icon: FileText },
                      { k: "billing_reference", ph: u.billingRef, icon: FileText },
                    ].map((f) => (
                      <label key={f.k} className="block">
                        <span className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2 block flex items-center gap-2">
                          <f.icon size={11} /> {f.ph}
                        </span>
                        <input value={form[f.k]} onChange={(e) => set(f.k, e.target.value)}
                               data-testid={`book-${f.k}`}
                               className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-2 text-sm focus:border-bronze transition-colors" />
                      </label>
                    ))}
                  </div>
                </StepRow>
              </div>
            )}

            {/* STEP 4 — SUMMARY ---------------------------------------- */}
            {step === 4 && (
              <div className="fadeUp space-y-8">
                <div className="glass border border-brand-border p-8 space-y-6">
                  <p className="text-[10px] tracking-widest2 uppercase text-bronze">— {u.yourTrip}</p>
                  <SummaryRow label={u.steps[0]} value={
                    form.service === "by_hour" ? `${u.byHour} · ${form.hours} h` :
                    form.service === "full_day" ? `${u.full} · ${form.hours} h` :
                    `${form.origin} → ${form.stops.map((s) => s.address).join(" → ") + (form.stops.length ? " → " : "")}${form.destination}`
                  } />
                  <SummaryRow label={u.when} value={new Date(form.pickup).toLocaleString(lang, { dateStyle: "full", timeStyle: "short" })} />
                  <SummaryRow label={u.passengers} value={`${form.passengers}`} />
                </div>

                <WaitTimeNote variant="card" />

                <div className="space-y-4">
                  <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">— {u.contactTitle}</p>
                  <input value={form.name} onChange={(e) => set("name", e.target.value)}
                         placeholder={u.name} data-testid={TID.book.name}
                         className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 text-lg focus:border-bronze transition-colors" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                           placeholder={u.email} data-testid={TID.book.email}
                           className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 focus:border-bronze transition-colors" />
                    <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                           placeholder={u.phone} data-testid={TID.book.phone}
                           className="w-full bg-transparent border-b border-brand-border text-brand-platinum py-3 focus:border-bronze transition-colors" />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.additional_charges_authorized}
                         onChange={(e) => set("additional_charges_authorized", e.target.checked)}
                         data-testid="auth-extras"
                         className="mt-1 h-4 w-4 accent-bronze" />
                  <span className="text-xs text-brand-silver leading-relaxed">{u.additionalAuth}</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.terms_accepted}
                         onChange={(e) => set("terms_accepted", e.target.checked)}
                         data-testid="book-terms"
                         className="mt-1 h-4 w-4 accent-bronze" />
                  <span className="text-xs text-brand-silver leading-relaxed">{u.terms}</span>
                </label>
              </div>
            )}

            {/* NAV -------------------------------------------------------- */}
            <div className="mt-12 flex items-center justify-between gap-4">
              <button type="button" onClick={prev} disabled={step === 0}
                      data-testid="nav-back"
                      className={`btn-pill ${step === 0 ? "opacity-30 cursor-not-allowed" : ""}`}>
                <ArrowLeft size={14} /> {u.back}
              </button>
              {step < 4 ? (
                <button type="button" onClick={next} disabled={!canProceed()}
                        data-testid="nav-next"
                        className={`btn-pill btn-pill--solid ${!canProceed() ? "opacity-40 cursor-not-allowed" : ""}`}>
                  {u.next} <ArrowRight size={14} />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={submitting}
                        data-testid={TID.book.submit}
                        className="btn-pill btn-pill--solid">
                  {submitting ? <><Loader2 size={14} className="animate-spin" /> {bk.redirecting}</>
                             : <>{u.pay} · CHF {totalCHF?.toFixed(2) || "—"} <ArrowRight size={14} /></>}
                </button>
              )}
            </div>
          </div>

          {/* ============================================================ */}
          {/* RIGHT: MAP + LIVE PRICE (sticky on desktop)                   */}
          {/* ============================================================ */}
          <aside className="lg:sticky lg:top-32 space-y-4">
            {showRoute && (
              <div className="border border-brand-border overflow-hidden">
                <RouteMap
                  origin={form.origin_coords || (form.origin ? { address: form.origin } : null)}
                  destination={form.destination_coords || (form.destination ? { address: form.destination } : null)}
                  height={360}
                />
              </div>
            )}

            {/* LIVE PRICE PANEL (glass) */}
            <div className="glass border border-brand-border p-6 space-y-5" data-testid="live-quote">
              {quoting ? (
                <div className="flex items-center gap-3 text-brand-silver text-sm">
                  <Loader2 size={14} className="animate-spin" /> {u.calculating}
                </div>
              ) : form.service === "full_day" ? (
                <div>
                  <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">{u.full}</p>
                  <p className="text-4xl font-light text-brand-platinum tabular-nums">CHF 980<span className="text-sm text-brand-dim ml-2">/ 8h</span></p>
                </div>
              ) : quote ? (
                <>
                  <div className="flex items-baseline justify-between">
                    <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">{u.total}</p>
                    <p className="text-4xl font-light text-brand-platinum tabular-nums">CHF {totalCHF?.toFixed(2)}</p>
                  </div>
                  {distanceKm !== null && distanceKm !== undefined && (
                    <div className="grid grid-cols-2 gap-4 text-xs pt-4 border-t border-brand-border">
                      <div>
                        <p className="text-brand-silver text-[10px] tracking-widest2 uppercase mb-1">{u.distance}</p>
                        <p className="text-brand-platinum text-lg tabular-nums">{distanceKm} km</p>
                      </div>
                      <div>
                        <p className="text-brand-silver text-[10px] tracking-widest2 uppercase mb-1">{u.duration}</p>
                        <p className="text-brand-platinum text-lg tabular-nums">{durationMin} min</p>
                      </div>
                      {trafficMin && (
                        <div>
                          <p className="text-brand-silver text-[10px] tracking-widest2 uppercase mb-1">{u.trafficDur}</p>
                          <p className="text-brand-platinum text-lg tabular-nums">{trafficMin} min</p>
                        </div>
                      )}
                      {etaIso && (
                        <div>
                          <p className="text-brand-silver text-[10px] tracking-widest2 uppercase mb-1">{u.eta}</p>
                          <p className="text-brand-platinum text-lg tabular-nums">
                            {new Date(etaIso).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {form.service === "by_hour" && (
                    <div className="pt-4 border-t border-brand-border text-xs">
                      <p className="text-brand-silver">{u.hoursPack} · {form.hours} h × CHF 110</p>
                    </div>
                  )}
                  {quote.swissalpenline?.hour_supplement_pct > 0 && (
                    <p className="text-[11px] text-bronze">
                      +{quote.swissalpenline.hour_supplement_pct}% · {quote.swissalpenline.hour_tier}
                      {quote.swissalpenline.weekend_applied ? ` · +${quote.swissalpenline.weekend_supplement_pct}% weekend` : ""}
                    </p>
                  )}

                  {/* Wait-time policy — Fase D: exposed to passenger before payment */}
                  <div className="pt-2 border-t border-brand-border/50">
                    <WaitTimeNote />
                  </div>

                  {/* Policy notice — Fase C: dispatch-rules constraint messages
                      (winter altitude, after-hours minimum, closure days).
                      Frontend surfaces the reason + intelligent alternatives. */}
                  {quote.policy && !quote.policy.accepted && (
                    <div className="mt-4 border border-bronze/50 bg-bronze/[0.06] p-4 space-y-3" data-testid="policy-warning">
                      <div className="flex items-start gap-3">
                        <AlertTriangle size={16} className="text-bronze shrink-0 mt-0.5" />
                        <p className="text-xs text-brand-platinum leading-relaxed">
                          {quote.policy.message_en}
                        </p>
                      </div>
                      {Array.isArray(quote.policy.alternatives) && quote.policy.alternatives.length > 0 && (
                        <div className="flex flex-col gap-2 pl-7">
                          {quote.policy.alternatives.map((alt, i) => {
                            if (alt.kind === "time") {
                              const iso = alt.when_iso;
                              const label = new Date(iso).toLocaleString(lang, {
                                weekday: "short", day: "2-digit", month: "short",
                                hour: "2-digit", minute: "2-digit",
                              });
                              return (
                                <button key={i} type="button"
                                        onClick={() => { set("pickup", isoLocal(iso)); goto(1); }}
                                        data-testid={`policy-alt-time-${i}`}
                                        className="btn-pill justify-center text-xs">
                                  <Clock size={12} /> {label} <ArrowRight size={12} />
                                </button>
                              );
                            }
                            if (alt.kind === "public_transport") {
                              return (
                                <p key={i} className="text-[11px] text-brand-silver leading-relaxed">
                                  <MapPin size={11} className="inline mr-1 text-bronze" />
                                  We can drive you to <span className="text-brand-platinum">{alt.lower_place}</span>
                                  {alt.transport && <> — continue with <span className="text-brand-platinum">{alt.transport}</span> to {alt.final_leg}.</>}
                                </p>
                              );
                            }
                            if (alt.kind === "external_link") {
                              return (
                                <a key={i} href={alt.url} target="_blank" rel="noreferrer"
                                   data-testid={`policy-alt-link-${i}`}
                                   className="text-[11px] tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum underline underline-offset-2">
                                  Check MeteoSwiss →
                                </a>
                              );
                            }
                            return null;
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Preferred Partner Benefit — optional code applied at checkout */}
                  <div className="pt-4 border-t border-brand-border">
                    {!partnerBenefit ? (
                      <>
                        <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">Partner code (optional)</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={partnerCode}
                            onChange={(e) => { setPartnerCode(e.target.value.toUpperCase()); setPartnerError(null); }}
                            placeholder="e.g. HOTEL-SCHWEIZERHOF"
                            data-testid="partner-code-input"
                            className="flex-1 bg-brand-bg border border-brand-border text-brand-platinum px-3 py-2 text-xs focus:border-bronze focus:outline-none tracking-widest2 uppercase"
                          />
                          <button
                            type="button"
                            onClick={applyPartner}
                            disabled={partnerApplying || !partnerCode.trim()}
                            data-testid="partner-code-apply"
                            className="text-[10px] tracking-widest2 uppercase border border-brand-border px-3 py-2 hover:border-bronze transition-colors disabled:opacity-40"
                          >
                            {partnerApplying ? "…" : "Apply"}
                          </button>
                        </div>
                        {partnerError && <p className="text-[11px] text-red-400 mt-2">{partnerError}</p>}
                      </>
                    ) : (
                      <div data-testid="partner-benefit-applied" className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] tracking-widest2 uppercase" style={{ color: "#B08D2F" }}>
                            {partnerBenefit.partner_type ? partnerBenefit.partner_type.toUpperCase() + " · " : ""}{partnerBenefit.partner_name}
                          </p>
                          <button type="button" onClick={clearPartner} className="text-[10px] tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum">
                            Remove
                          </button>
                        </div>
                        <div className="text-xs text-brand-silver flex justify-between">
                          <span>Public price</span>
                          <span className="line-through opacity-60 tabular-nums">CHF {partnerBenefit.public_price_chf.toFixed(2)}</span>
                        </div>
                        <div className="text-xs flex justify-between" style={{ color: "#B08D2F" }}>
                          <span>Partner benefit</span>
                          <span className="tabular-nums">− CHF {partnerBenefit.benefit_amount_chf.toFixed(2)}</span>
                        </div>
                        <div className="text-sm text-brand-platinum flex justify-between pt-2 border-t border-brand-border">
                          <span>Final price</span>
                          <span className="tabular-nums font-medium" data-testid="partner-final-price">CHF {partnerBenefit.final_price_chf.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-brand-silver text-sm">{bk.enterRoute}</p>
              )}
            </div>

            <div className="border border-brand-border p-4 flex items-start gap-3">
              <Shield size={14} className="text-bronze mt-0.5 shrink-0" />
              <p className="text-[11px] text-brand-silver leading-relaxed">
                <span className="text-brand-platinum block mb-1">{u.singleCarNote}</span>
                {bk.singleCar.body}
              </p>
            </div>
          </aside>

        </div>
      </div>

      {/* Local styles */}
      <style>{`
        .fadeUp { animation: fadeUp 0.55s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .glass { background: rgba(20, 20, 20, 0.55); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); }
      `}</style>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 pb-4 border-b border-brand-border/60 last:border-0 last:pb-0">
    <span className="text-[10px] tracking-widest2 uppercase text-brand-silver shrink-0">{label}</span>
    <span className="text-sm text-brand-platinum text-right">{value || "—"}</span>
  </div>
);

export default Reservar;
